import { Center, Column, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { EmptyScreen } from '~/commonComponents';
import { useCallback, useRef } from 'react';
import { colors } from '@tempmee/tailwind';
import { CreatePostingButton } from '~/components/PermanentJobsV2/CreatePostingButton';
import { IDraftJobPostListViewEntity } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import BriefcaseEmptyScreenIcon from '~/images/briefcase-empty-screen-icon.svg';
import { DraftJobCard } from '../JobCard';
import { DeleteDraftPopupConfirmation } from './DeleteDraftPopup';

interface IDraftTableProps {
  data: IDraftJobPostListViewEntity[];
  isLoading: boolean;
}

export const DraftTable = ({ data, isLoading }: IDraftTableProps) => {
  const [isDeleteOpen, controlsDelete] = useToggle();
  const targetedJobPost = useRef<string | undefined>(undefined);

  const isEmptyData = !isLoading && data.length === 0;

  const handleDeleteClicked = useCallback(
    (candidate: string) => {
      targetedJobPost.current = candidate;
      controlsDelete.setOn();
    },
    [controlsDelete],
  );

  return (
    <>
      {isEmptyData ? (
        <EmptyScreen
          Icon={() => (
            <img
              alt="briefcase-empty-screen-icon"
              src={BriefcaseEmptyScreenIcon}
              height={170}
              width={170}
            />
          )}
          title="No drafts yet!"
          description={
            <Column align="center" gap={12}>
              <Typography as="span" color={colors.neutral[500]}>
                You don’t have any saved drafts. Start creating a<br />
                job post, and your progress will be saved here
                <br />
                until you’re ready to publish.
              </Typography>
              <CreatePostingButton />
            </Column>
          }
        />
      ) : (
        <>
          <Column align="center">
            <Column gap={4} className="w-full max-w-[672px]">
              {!isLoading &&
                data.map((it: IDraftJobPostListViewEntity) => (
                  <DraftJobCard key={it.id} {...it} onDeleteDraft={handleDeleteClicked} />
                ))}
            </Column>
          </Column>
          {isLoading && (
            <Center style={{ marginBlock: 32 }}>
              <CenteredSpinner />
            </Center>
          )}
        </>
      )}
      {isDeleteOpen && targetedJobPost.current && (
        <DeleteDraftPopupConfirmation
          controls={controlsDelete}
          jobPostingId={targetedJobPost.current}
        />
      )}
    </>
  );
};
